import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import { themevals } from "theme/themevals";
import { getProducts, deleteProduct, restoreProduct } from "api/Products";
import { trackActivity } from "api/ActivityTracker";

import Header from "components/ui/Header";
import Page from "components/ui/Page";
import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import TableData from "components/ui/TableData";
import { PlusIcon, QuestionMarkIcon, TrashIcon, CheckMarkIcon } from "components/svg";
import EllipsisIcon from "components/svg/icons/EllipsisIcon";
import DropDownMenu from "components/forms/DropDown/DropDownMenu";
import BasicModal from "components/ui/BasicModal";
import P from "components/typography/P";
import moment from "moment-timezone";
import { Badge, Box } from "theme-ui";
import UserContext from "context/User";

const columnHelper = createColumnHelper();

const createColumn = (id: string, title: string, accessor: (row: any) => any, cell?: (info: any) => any, options = {}) =>  
  columnHelper.accessor(accessor, { id, cell: cell || ((info) => info.getValue()), header: () => <span>{title}</span>, ...options });


export default function Products () {
  const navigate = useNavigate();

  const [refreshData, setRefreshData] = useState<boolean>(false);  

  const [confirmModal, setConfirmModal] = useState<any>(false);
  const [confirmSubmit, setConfirmSubmit] = useState<boolean>(false);

  const toast = useToast();
  const { user } = useContext<any>(UserContext);
  const lastTrackedQuery = useRef<string | null>(null);

  const confirmDelete = async (del:any) => { 
    setConfirmSubmit(true);   
    const res = await deleteProduct({ data: del });
    if(res?.success){ 
      toast.add("Product deactivated.","var(--theme-ui-colors-success)");
      void trackActivity({
        module: "products",
        action: "deactivate_product",
        actionType: "update",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "product",
        entityId: del?.id ?? null,
        metadata: {
          source: "products_list",
        },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      setRefreshData(true);    
      setTimeout(() => { setRefreshData(false) }, 100);   
    }else{
      toast.add(res?.message ? res.message : "Error deleting product.","var(--theme-ui-colors-danger)");
    } 
    setConfirmSubmit(false);   
    setConfirmModal(false)          
  };  

  const deleteModal = async (del?:any) => {
    setConfirmModal(<BasicModal
      title="Deactivate product?"    
      cancel_click={() => setConfirmModal(false)}
      cancel_text="No, cancel"   
      size={"sm"}
      icon={<QuestionMarkIcon fill={"#818181"} height="30px"/>}
      actions={<>
        <BasicButton
          type="button"    
          scheme="danger"      
          onClick={() => confirmDelete(del)} 
          $submitting={confirmSubmit}
          $iconHeight="14px"
          $iconWidth="14px"
          $loadingColor="#fff"
          keepText={true}
        >
          {!confirmSubmit && <TrashIcon fill={"#fff"}/>}        
          {"Deactivate"}
        </BasicButton>
      </>}    
    >
      <P lineHeight="22px">Are you sure you want to deactivate <strong style={{color:"var(--theme-ui-colors-black)"}}>{del?.name}</strong>?</P>
    </BasicModal>);
  }

  useEffect(() => {    
    document.title = process.env.REACT_APP_NAME + " | Products"        
  }, []); 

  const confirmReactivate = async (product: any) => {
    setConfirmSubmit(true);
    try {
      const res = await restoreProduct({ id: product?.id });
      if (res?.success) {
        toast.add("Product reactivated.","var(--theme-ui-colors-success)");
        void trackActivity({
          module: "products",
          action: "reactivate_product",
          actionType: "update",
          userId: user?.id || null,
          companyId: user?.company?.id || null,
          entityType: "product",
          entityId: product?.id ?? null,
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
        setRefreshData(true);
        setTimeout(() => { setRefreshData(false); }, 100);
      } else {
        toast.add(res?.message ? res.message : "Error reactivating product.","var(--theme-ui-colors-danger)");
      }
    } catch (error: any) {
      toast.add(error?.message ? error.message : "Error reactivating product.","var(--theme-ui-colors-danger)");
    } finally {
      setConfirmSubmit(false);
      setConfirmModal(false);
    }
  };

  const submitReactivate = async (product?: any) => {
    setConfirmModal(
      <BasicModal
        title="Reactivate product?"
        cancel_click={() => setConfirmModal(false)}
        cancel_text="No, cancel"
        size={"sm"}
        icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
        actions={
          <BasicButton
            type="button"
            onClick={() => confirmReactivate(product)}
            $submitting={confirmSubmit}
            $iconHeight="14px"
            $iconWidth="14px"
            $loadingColor="#fff"
            keepText={true}
            styles={{
              margin: "0",
              backgroundColor: themevals.colors.success,
              borderColor: themevals.colors.success,
              color: "#fff",
            }}
          >
            {!confirmSubmit && <CheckMarkIcon fill={"#fff"} />}
            Reactivate
          </BasicButton>
        }
      >
        <P lineHeight="22px">
          Are you sure you want to reactivate <strong style={{ color: "var(--theme-ui-colors-black)" }}>{product?.name}</strong>?
        </P>
      </BasicModal>
    );
  };

  return (
    <>
      <Header></Header>      
      <Page title="Products" subtitle="Manage products for your company.">                
        <TableData 
          api={getProducts} 
          nothingFoundText="No products found."
          columns={[
            createColumn("name","Name", (row) => row?.name, (info) => {
              const product = info.row.original;
              const isDeleted = Boolean(product?.deletedBy) || product?.status === 0;
              return (
                <Box
                  sx={{color:"#000", cursor: isDeleted ? "default" : "pointer"}}
                  onClick={() => {
                    if (!isDeleted) {
                      navigate(`/products/${product.id}`);
                    }
                  }}
                >
                  {info.getValue()}
                </Box>
              );
            }),
            createColumn("description","Description", (row) => row?.description),
            createColumn("deletedAt","Status", (row) => row?.status, (info) => (() => {
              switch(info.getValue()){
                case(1):
                  return <Badge as="span" variant="success">Active</Badge>;                      
                default:
                  return <Badge as="span" variant="danger">Inactive</Badge>;            
            }})()),
            createColumn("addon","Add-on", (row) => row?.addon, (info) => (() => {
              switch(info.getValue()){
                case(true):
                  return <CheckMarkIcon fill={themevals.colors.success} width="20px" height="20px" />;                      
                default:
                  return <span></span>;            
            }})()),
            createColumn("updatedAt","Last Updated", (row) => row?.updatedAt, (info) => moment(info.getValue()).format("MMM D, YYYY")),
            createColumn("createdAt","Date Added", (row) => row?.createdAt, (info) => moment(info.getValue()).format("MMM D, YYYY")),
            createColumn("lastUpdateBy","Updated By", (row) => row?.lastUpdateBy, (info) => `${info?.getValue()?.firstName || ""} ${info?.getValue()?.lastName || ""}`),
            createColumn("actions","", () => "actions", (info) => {      
              const product = info.row.original;
              const isDeleted = Boolean(product?.deletedBy) || product?.status === 0;
              const items = isDeleted
                ? [
                    {
                      label: "Reactivate",
                      onClick: () => submitReactivate(product),
                      style: { color: themevals.colors.success },
                    },
                  ]
                : [
                    { label: "Edit", onClick: () => navigate(`/products/${product.id}`) },
                    { label: "Deactivate", onClick: () => deleteModal(product), style: { color: "var(--theme-ui-colors-danger)" } },
                  ];
              return (
                <Box>
                  <DropDownMenu
                    toggleComponent={<EllipsisIcon fill={"#818181"} width="20px" height="20px" />}
                    items={items}
                  />
                </Box>
              );
            }, { header: () => null, enableSorting: false, meta: { style: { width: "10px" } } }),
          ]}
          refresh={refreshData} 
          returnQuery={(query: any) => {
            if (!query) return;

            const signature = JSON.stringify({
              page: query?.pageNum ?? null,
              limit: query?.recordsPer ?? null,
            });

            if (lastTrackedQuery.current === signature) {
              return;
            }
            lastTrackedQuery.current = signature;

            void trackActivity({
              module: "products",
              action: "view_products_list",
              actionType: "view",
              userId: user?.id || null,
              companyId: user?.company?.id || null,
              entityType: "product_list",
              entityId: null,
              metadata: {
                page: query?.pageNum ?? null,
                limit: query?.recordsPer ?? null,
              },
              userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
            });
          }}
          actions={[<BasicButton key="1" onClick={() => navigate('/products/create')} $iconHeight="10px"><PlusIcon fill="#fff"/>Add product</BasicButton>]} 
          title={"All Products"}
        />
      </Page>      
      {confirmModal}
    </>
  );
}
